import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { useMemo, useState } from "react";
import { predatorThresholdQueryOptions } from "@/lib/client/apex";
import { cn } from "@/lib/utils";
import { DefaultImageCard } from "@/features/home/components/image-card";
import { Loading } from "@/features/shared/components/loading";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const PlatformMap = {
  PC: "PC",
  PS4: "PS",
  X1: "Xbox",
  // SWITCH: 'Switch',
};

const PlatformColors = {
  PC: "text-blue-400",
  PS4: "text-indigo-400",
  X1: "text-green-400",
  SWITCH: "text-red-400",
};

export function PredatorThreshold() {
  const [selectedPlatform, setSelectedPlatform] = useState<keyof typeof PlatformMap>("PC");
  const { data, isLoading } = useQuery(predatorThresholdQueryOptions);

  const currentData = useMemo(() => {
    return data?.RP?.[selectedPlatform];
  }, [data, selectedPlatform]);

  if (!data || isLoading || !currentData) return <Loading size={4} className="mx-auto" />;

  const isValidThreshold = currentData.foundRank !== -1 && currentData.val > 16000;

  return (
    <DefaultImageCard image="/images/home/predator.webp">
      <div className="flex flex-col gap-2 text-end text-white text-shadow-md">
        <Tabs
          defaultValue={selectedPlatform}
          className="-mt-3 self-end"
          onValueChange={(value) => setSelectedPlatform(value as keyof typeof PlatformMap)}
        >
          <TabsList className="grid grid-cols-3 bg-transparent p-0 justify-self-end text-xs">
            {Object.entries(PlatformMap).map(([key, name]) => (
              <TabsTrigger
                key={key}
                value={key}
                className="px-2 py-1 text-xs text-white data-[state=active]:text-foreground"
              >
                {name}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <h1 className="text-3xl font-bold">
          <span className="text-xs text-white/60 font-mono mr-2">猎杀门槛</span>
          <span className={cn(PlatformColors[selectedPlatform], "font-mono")}>
            {PlatformMap[selectedPlatform]}
          </span>
        </h1>

        <div className="text-2xl font-bold">
          {isValidThreshold ? (
            <>
              <span className="text-orange-300 font-mono">{currentData.val.toLocaleString()}</span>
              <span className="text-sm text-white/60 ml-2">RP</span>
            </>
          ) : (
            <span className="text-gray-400">数据不可用</span>
          )}
        </div>

        <p className="text-sm text-white/60 font-mono">
          大师+ 总人数: {currentData.totalMastersAndPreds.toLocaleString()}
        </p>

        <p className="text-xs text-white/50 font-mono">
          更新时间: {format(currentData.updateTimestamp * 1000, "MM-dd HH:mm")}
        </p>
      </div>
    </DefaultImageCard>
  );
}
